import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Users, BookOpen, IndianRupee, TrendingUp } from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').trim();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : null;

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState({
    total_students: 0,
    total_enrollments: 0,
    completed_payments: 0
  });
  const [students, setStudents] = useState([]);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      if (!API) return;
      const [statsRes, studentsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/students`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold font-heading gradient-text mb-2" data-testid="super-admin-title">
            Super Admin Dashboard
          </h1>
          <p className="text-gray-600">Manage your platform</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-2xl shadow-lg text-white" data-testid="stat-students">
            <Users className="w-12 h-12 mb-4 opacity-80" />
            <h3 className="text-3xl font-bold mb-2">{stats.total_students}</h3>
            <p className="text-blue-100">Total Students</p>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-2xl shadow-lg text-white" data-testid="stat-enrollments">
            <BookOpen className="w-12 h-12 mb-4 opacity-80" />
            <h3 className="text-3xl font-bold mb-2">{stats.total_enrollments}</h3>
            <p className="text-green-100">Total Enrollments</p>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-red-600 p-6 rounded-2xl shadow-lg text-white" data-testid="stat-payments">
            <IndianRupee className="w-12 h-12 mb-4 opacity-80" />
            <h3 className="text-3xl font-bold mb-2">{stats.completed_payments}</h3>
            <p className="text-orange-100">Completed Payments</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold font-heading mb-6">Recent Students</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Phone</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {students.slice(0, 10).map((student, index) => (
                  <tr key={student.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`student-row-${index}`}>
                    <td className="py-3 px-4">{student.full_name}</td>
                    <td className="py-3 px-4 text-gray-600">{student.email}</td>
                    <td className="py-3 px-4 text-gray-600">{student.phone}</td>
                    <td className="py-3 px-4">
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        {student.verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}